<script setup>
  import {computed, inject, ref, watch} from "vue";

  import ComponentBadge from "@components/Badges/Badge.vue";
  import ComponentRowOriginFromMergeBadge from "@components/Badges/RowOriginFromMergeBadge.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentDosageInput from "@components/Inputs/DosageInput.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {dosageUnits, medicationMeal, medicationUsage} from "@enums/medicationEnums.js";
  import {additionalinformationDecreed, currentSource} from "@pages/Records/Components/Result/Enums.js";
  import MedicationComponentIndicationDisplay from "@pages/Records/Components/Sections/MedicationBar/Components/Indications/IndicationDisplay.vue";
  import MedicationComponentRowControl from "@pages/Records/Components/Sections/MedicationBar/Components/RowControl.vue";
  import {checkAllInfos} from "@pages/Records/Components/Sections/MedicationBar/Utils/medicationConsistencyCheck.js";
  import {MedButton, MedIcon, MedLink, MedTooltip} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "MedicationTableRow",
  });

  const props = defineProps({
    isEditable: {
      type: Boolean,
      required: true,
    },
    medication: {
      type: Object,
      required: true,
    },
    isDragging: {
      type: Boolean,
      required: true,
    },
  });

  const emit = defineEmits(["updateMedications", "cancelUpdateMedications"]);

  const openDialog = inject("medicationOpenDialog");
  const deleteDialog = inject("recordEditDeleteRowDialog");
  const recordEditHandleValidDosage = inject("recordEditHandleValidDosage");
  const recordEditHandleInvalidDosage = inject("recordEditHandleInvalidDosage");
  const isMergeRecordTransfer = inject("isMergeRecordTransfer");

  const expanded = ref(false);

  const medicationForm = ref(props.medication);

  const hasAdditionalInformation = computed(() => {
    if (medicationForm.value) {
      return checkAllInfos(medicationForm.value);
    }

    return false;
  });

  const currentDosageUnit = computed(() => {
    return {
      unit: medicationForm.value.dosage.selected_unit,
      keyDoe: medicationForm.value.dosage.selected_unit_key_doe,
    };
  });

  const currentDosageUnitList = computed(() => {
    return medicationForm.value.core.unitList !== undefined && medicationForm.value.core.unitList.length > 0
      ? medicationForm.value.core.unitList
      : dosageUnits;
  });

  /**
   * A dosage unit that was selected in the past but is no longer offered by the core data
   * (e.g. because it has been blocked in the meantime) is kept, but has to be replaced by the user.
   */
  const isBlockedDosageUnit = computed(() => {
    if (currentDosageUnit.value.keyDoe === null || currentDosageUnit.value.keyDoe === undefined) {
      return false;
    }

    return !currentDosageUnitList.value.some((unit) => Number(unit.keyDoe) === Number(currentDosageUnit.value.keyDoe));
  });

  const blockedDosageUnitHint = computed(() =>
    isBlockedDosageUnit.value ? `"${currentDosageUnit.value.unit}" nicht mehr verfügbar.` : null,
  );

  const dosageUnitTooltip = computed(() =>
    isBlockedDosageUnit.value
      ? `"${currentDosageUnit.value.unit}" ist nicht mehr verfügbar. Bitte wählen Sie eine alternative Dosiereinheit.`
      : currentDosageUnit.value.unit,
  );

  const placeholderTextLabel = computed(() => (props.isEditable ? "Bitte auswählen" : ""));

  const isDiscontinued = computed(() => !!medicationForm.value.additionalinformation?.discontinued);

  watch(
    () => props.medication,
    () => {
      medicationForm.value = props.medication;
    },
    {deep: true},
  );

  const updateUsage = (newUsage) => {
    medicationForm.value.usage = newUsage;
    emit("updateMedications");
  };

  const updateDosageUnit = (newDosageKeyDoe) => {
    medicationForm.value.dosage.selected_unit_key_doe = newDosageKeyDoe.keyDoe;
    medicationForm.value.dosage.selected_unit = newDosageKeyDoe.unit;
    emit("updateMedications");
  };

  const updateMeal = (newMeal) => {
    medicationForm.value.dosage.meal = newMeal;
    emit("updateMedications");
  };

  const openDetails = () => {
    openDialog("refEditDetailDialog", {medication: medicationForm.value});
  };

  const getMeal = (meal) => {
    return medicationMeal[meal];
  };

  const handleDosageChange = (newValues, type) => {
    const asFraction = type + "_as_fraction";

    medicationForm.value.dosage[type] = newValues.value;
    medicationForm.value.dosage[asFraction] = newValues.as_fraction;

    recordEditHandleValidDosage(newValues.id);

    emit("updateMedications");
  };

  const handleInvalidDosage = (dosageId) => {
    recordEditHandleInvalidDosage(dosageId, () => emit("cancelUpdateMedications"));
  };

  const showDeleteConfirmDialog = () => {
    deleteDialog({routeName: "medications", payload: {medication: medicationForm.value.id}});
  };

  const detailDialogIndications = () => {
    if (props.isEditable) {
      openDialog("refIndicationDialog", {
        medication: medicationForm.value,
        medicationKeyFam: medicationForm.value.abdata_key_fam,
        medicationHashId: medicationForm.value.id,
      });
    }
  };

  const detailsDeviantMedication = computed(() => {
    let parts = [];

    // usage
    if (
      props?.medication?.deviantmedication?.usage !== undefined &&
      props?.medication?.deviantmedication?.usage !== null
    ) {
      parts.push(medicationUsage[props?.medication?.deviantmedication?.usage]);
    }

    // dosagealternative.text or dosage_string_formatted
    if (props?.medication?.deviantmedication?.dosage?.dosagealternative?.text) {
      parts.push(props?.medication?.deviantmedication?.dosage?.dosagealternative?.text);
    } else if (props?.medication?.deviantmedication?.dosage?.dosage_string_formatted) {
      parts.push(
        props?.medication?.deviantmedication?.dosage?.dosage_string_formatted +
          " " +
          props?.medication?.deviantmedication?.dosage?.selected_unit,
      );
    }

    // indication(s)
    if ((props?.medication?.deviantmedication?.indications ?? []).length > 0) {
      let text = props?.medication?.deviantmedication?.indications.length === 1 ? "Indikation: " : "Indikationen: ";

      text += props?.medication?.deviantmedication?.indications
        .map((indication) => {
          return indication.name;
        })
        .join(", ");

      parts.push(text);
    }

    return parts.length === 0 ? "-" : parts.join("; ");
  });

  const detailsSatisfaction = computed(() => {
    let text = "";
    switch (props.medication?.additionalinformation?.satisfaction) {
      case "uaw":
        text += "Unzufrieden wegen UAW / NW";
        break;
      case "effectiveness":
        text += "Unzufrieden mit Wirksamkeit";
        break;
      case "handling":
        text += "Unzufrieden mit Handhabung";
        break;
      default:
        return "-";
    }

    if (props.medication?.additionalinformation?.satisfactioncomment) {
      text += ": " + props.medication?.additionalinformation?.satisfactioncomment;
    }

    return text;
  });

  const detailsSideEffects = computed(() => {
    if ((props.medication?.additionalinformation?.sideeffects ?? []).length === 0) {
      return "-";
    }

    return props.medication?.additionalinformation?.sideeffects
      .map((sideeffect) => {
        switch (sideeffect) {
          case "storage":
            return "falsche Lagerung";
          case "expired":
            return "FAM verfallen";
          case "wronguse":
            return "falsche Anwendung";
        }
      })
      .join(", ");
  });

  const detailsRequirements = computed(() => {
    switch (props.medication?.additionalinformation?.requirements) {
      case "splitable":
        return "Teilbar";
      case "mortarable":
        return "Mörserbar";
      case "suspendable":
        return "Suspendierbar";
      default:
        return "-";
    }
  });

  const detailsSource = computed(() => {
    if ((props.medication?.additionalinformation?.source ?? []).length === 0) {
      return "-";
    }

    return props.medication?.additionalinformation?.source
      .map((source) => {
        return currentSource[source];
      })
      .join(", ");
  });

  const detailsDecreed = computed(() => {
    let parts = [];

    if (props.medication?.additionalinformation?.decreed) {
      parts.push(additionalinformationDecreed[props.medication?.additionalinformation?.decreed]);
    }

    const since = props.medication?.additionalinformation?.since;
    if (since) {
      const [year, month, day] = since.split("-");
      parts.push(`seit ${day}.${month}.${year}`);
    }

    const to = props.medication?.additionalinformation?.to;
    if (to) {
      const [year, month, day] = to.split("-");
      parts.push(`bis ${day}.${month}.${year}`);
    }

    return parts.length === 0 ? "-" : parts.join(" ");
  });
</script>

<template>
  <li
    class="grid grid-cols-[1fr_1fr_140px_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-x-2 border-b-1 border-gray-300 py-2"
    :class="[{'opacity-50': isDiscontinued}]"
    :data-test="getTestId('row')"
  >
    <!-- row 0 - optional origin when merging records -->
    <ComponentRowOriginFromMergeBadge :hashid="medicationForm.id" type="medications" class="col-span-12 ml-8" />

    <!-- row 1 - drag-handle or prefix slot, name and fi -->
    <div
      :class="
        cn('col-span-12 flex', {
          'mt-2': isMergeRecordTransfer,
        })
      "
    >
      <!-- name -->
      <!-- TODO rework with good layout -->
      <template v-if="isEditable">
        <div class="flex items-center">
          <slot name="prefix">
            <MedTooltip side="left" content="Zeile verschieben" :disabled="isDragging">
              <span
                role="button"
                tabindex="0"
                aria-label="Zeile verschieben"
                class="flex cursor-grab items-center active:cursor-grabbing"
                drag-handle
                @keydown.enter.prevent
                @keydown.space.prevent
              >
                <MedIcon size="lg" icon="drag_handle" />
              </span>
            </MedTooltip>
          </slot>

          <MedLink
            as="button"
            :class="cn('text-md mx-2 font-medium text-gray-900', {'line-through': isDiscontinued})"
            :data-test="getTestId('row-fam-link')"
            @click="openDetails"
          >
            <MedTooltip
              v-if="medicationForm.abdata_key_fam === null || medicationForm.abdata_key_fam === 0"
              content="nicht analysierbar"
            >
              <span class="cursor-pointer pr-1 text-mcred">**</span>
            </MedTooltip>

            <MedTooltip :content="medicationForm.fam">
              <span>{{ medicationForm.fam }}</span>
            </MedTooltip>
          </MedLink>
        </div>
      </template>

      <template v-else>
        <div class="mx-2 flex items-center">
          <MedTooltip
            v-if="medicationForm.abdata_key_fam === null || medicationForm.abdata_key_fam === 0"
            content="nicht analysierbar"
          >
            <span class="cursor-pointer pr-1 text-mcred">**</span>
          </MedTooltip>

          <MedTooltip :content="medicationForm.fam">
            <button
              class="line-clamp-1"
              :class="{'line-through': isDiscontinued}"
              :data-test="getTestId('row-fam-link')"
              @click.prevent="openDetails"
            >
              {{ medication.fam }}
            </button>
          </MedTooltip>
        </div>
      </template>

      <!-- fi button -->
      <div v-if="medicationForm.core.redhand" class="relative align-middle">
        <MedTooltip content="Rote-Hand-Brief (ggf. weitere Dokumente)">
          <MedButton
            aria-label="Fachinformationen und Rote-Hand-Brief anzeigen"
            rounded="full"
            size="xs"
            class="size-7.5 shrink-0 bg-gray-200 p-1 text-sm hover:bg-gray-300"
            :data-test="getTestId('row-fi-button')"
            @click="openDialog('refCoreInfoDialog', medicationForm)"
          >
            FI
          </MedButton>

          <MedIcon size="xs" icon="pan_tool" class="absolute -top-1 -right-1 text-sm text-mcred select-none" />
        </MedTooltip>
      </div>

      <div v-else class="flex items-center">
        <MedTooltip content="Fachinformation (ggf. weitere Dokumente)">
          <MedButton
            aria-label="Fachinformationen anzeigen"
            rounded="full"
            size="xs"
            class="size-7.5 shrink-0 bg-gray-200 p-1 text-sm hover:bg-gray-300"
            :data-test="getTestId('row-fi-button')"
            @click="openDialog('refCoreInfoDialog', medicationForm)"
          >
            FI
          </MedButton>
        </MedTooltip>
      </div>
    </div>

    <!-- row 2 - opt. 🔻 and gray text -->
    <div class="col-span-12 mt-1 flex w-full items-center" :class="{'ml-2': !isEditable}">
      <div v-if="isMergeRecordTransfer">
        <MedTooltip :content="expanded ? 'einklappen' : 'ausklappen'" side="left">
          <button
            class="flex cursor-pointer items-center rounded-full leading-none"
            :aria-label="expanded ? 'Einklappen' : 'Ausklappen'"
            :aria-expanded="expanded"
            @click="expanded = !expanded"
          >
            <MedIcon
              size="lg"
              icon="keyboard_arrow_down"
              :class="
                cn('transition-transform duration-200', {
                  'rotate-180': expanded,
                  'text-mcred': !expanded,
                })
              "
            />
          </button>
        </MedTooltip>
      </div>

      <div
        role="button"
        tabindex="0"
        :class="
          cn('data-cell-span cursor-pointer text-xs text-gray-500', {
            'line-through': medicationForm.additionalinformation?.discontinued,
            'ml-3': isEditable && isMergeRecordTransfer,
            'ml-8': isEditable && !isMergeRecordTransfer,
          })
        "
        :data-test="getTestId('row-open-details')"
        @click="openDetails"
        @keydown.enter="openDetails"
        @keydown.space.prevent="openDetails"
      >
        <template v-if="medicationForm.core.substancesText && medicationForm.core.substancesText.length > 0">
          {{ medicationForm.core.substancesText + " / " ?? "-" + " / " }}
        </template>

        <template v-if="medicationForm.form && medicationForm.form.length > 0">
          {{ medicationForm.form + " / " }}
        </template>

        <template v-if="medicationForm.selected_package_pzn && medicationForm.core.manufactureText">
          {{ medicationForm.core.manufactureText + " / " }}
        </template>

        <template v-if="medicationForm.core.selectedPackageLabel === 'außer Vertrieb'">
          {{ medicationForm.core.selectedPackageLabel }}
        </template>

        <template v-else>PZN: {{ medicationForm.core.selectedPackageLabel ?? "-" }}</template>

        <template v-if="medicationForm.core.package">{{ " / " + medicationForm.core.package }}</template>
      </div>
    </div>

    <!-- row 3 - col 1 / Art -->
    <div
      class="col-span-2"
      :class="[{'pointer-events-none line-through': isDiscontinued, 'ml-2': !isEditable, 'ml-8': isEditable}]"
    >
      <MedTooltip :content="medicationUsage[medicationForm.usage]" :disabled="medicationForm.usage === null">
        <ComponentSelect
          :id="'usage' + medicationForm.id"
          ref="usage"
          :name="'usage' + medicationForm.id"
          :model-value="medicationForm.usage"
          :options="medicationUsage"
          :class="{'line-through': isDiscontinued}"
          :nullable="true"
          :disabled="!isEditable"
          placeholder-text=""
          :test-id="getTestId('row-usage')"
          @change="updateUsage"
        />
      </MedTooltip>
    </div>

    <!-- row 3 - col 2 / Dosierung -->
    <div class="col-span-1" :class="[{'pointer-events-none line-through': isDiscontinued}]">
      <template v-if="medicationForm.dosage.dosagealternative === null">
        <div class="flex gap-1.5">
          <ComponentDosageInput
            :id="'dosageMorning' + medicationForm.id"
            :ref="'dosageMorning' + medicationForm.id"
            :name="'dosageMorning' + medicationForm.id"
            :value="medicationForm.dosage.morning"
            :as-fraction="medicationForm.dosage.morning_as_fraction"
            :disabled="!isEditable"
            :test-id="getTestId('row-usage-morning')"
            @input="handleDosageChange($event, 'morning')"
            @is-invalid="handleInvalidDosage"
          />

          <ComponentDosageInput
            :id="'dosageNoon' + medicationForm.id"
            :ref="'dosageNoon' + medicationForm.id"
            :name="'dosageNoon' + medicationForm.id"
            :value="medicationForm.dosage.noon"
            :as-fraction="medicationForm.dosage.noon_as_fraction"
            :disabled="!isEditable"
            :test-id="getTestId('row-usage-noon')"
            @input="handleDosageChange($event, 'noon')"
            @is-invalid="handleInvalidDosage"
          />

          <ComponentDosageInput
            :id="'dosageEvening' + medicationForm.id"
            :ref="'dosageEvening' + medicationForm.id"
            :name="'dosageEvening' + medicationForm.id"
            :value="medicationForm.dosage.evening"
            :as-fraction="medicationForm.dosage.evening_as_fraction"
            :disabled="!isEditable"
            :test-id="getTestId('row-usage-evening')"
            @input="handleDosageChange($event, 'evening')"
            @is-invalid="handleInvalidDosage"
          />

          <ComponentDosageInput
            :id="'dosageNight' + medicationForm.id"
            :ref="'dosageNight' + medicationForm.id"
            :name="'dosageNight' + medicationForm.id"
            :value="medicationForm.dosage.night"
            :as-fraction="medicationForm.dosage.night_as_fraction"
            :disabled="!isEditable"
            :test-id="getTestId('row-usage-night')"
            @input="handleDosageChange($event, 'night')"
            @is-invalid="handleInvalidDosage"
          />
        </div>
      </template>

      <template v-else>
        <MedTooltip :content="medicationForm.dosage.dosagealternative.text">
          <div class="relative">
            <div
              v-if="isEditable"
              role="button"
              tabindex="0"
              class="h-9 cursor-pointer items-center border-b-1 border-gray-300 p-2"
              :data-test="getTestId('row-dosage-open-details')"
              @click="openDetails()"
              @keydown.enter="openDetails()"
              @keydown.space.prevent="openDetails()"
            >
              <div class="truncate text-sm">Sonderdosierung</div>
            </div>

            <div
              v-else
              class="h-9 cursor-default items-center border-b-1 border-dashed border-gray-300 p-2"
              :data-test="getTestId('row-dosage-open-details')"
            >
              <div class="truncate text-sm text-gray-500!">Sonderdosierung</div>
            </div>

            <template v-if="!isDiscontinued">
              <div
                class="absolute top-0.5 -right-0.5 inline-flex size-4 cursor-help items-center justify-center rounded-full p-0 text-lg font-medium text-mcgreen"
              >
                <ComponentIcon class="fill-current">info</ComponentIcon>
              </div>
            </template>
          </div>
        </MedTooltip>
      </template>
    </div>

    <!-- row 3 - col 3 / Einheit -->
    <div class="col-span-2" :class="[{'pointer-events-none line-through': isDiscontinued}]">
      <div class="relative">
        <MedTooltip :content="dosageUnitTooltip" :disabled="currentDosageUnit.unit === null">
          <ComponentSelect
            :id="'dosageUnit' + medicationForm.id"
            ref="unitfield"
            :data-hash-id="medicationForm.id"
            :model-value="currentDosageUnit"
            :disabled="!isEditable"
            :options="currentDosageUnitList"
            :class="{'line-through': isDiscontinued}"
            key-value="keyDoe"
            key-name="unit"
            return-type="object"
            :placeholder-text="placeholderTextLabel"
            :validation="blockedDosageUnitHint"
            :test-id="getTestId('row-dosage-unit')"
            @change="updateDosageUnit"
          />
        </MedTooltip>

        <MedIcon
          v-if="isBlockedDosageUnit"
          size="xs"
          icon="warning"
          class="pointer-events-none absolute top-2.5 left-2 text-sm text-mcred select-none"
          :data-test="getTestId('row-dosage-unit-blocked-warning')"
        />
      </div>
    </div>

    <!-- row 3 - col 4 / Mahlzeit -->
    <div class="col-span-2" :class="[{'pointer-events-none line-through': isDiscontinued}]">
      <MedTooltip :content="getMeal(medicationForm.dosage.meal)" :disabled="medicationForm.dosage.meal === null">
        <ComponentSelect
          :id="'meal' + medicationForm.id"
          ref="meal"
          :name="'meal' + medicationForm.id"
          :disabled="!isEditable"
          :model-value="medicationForm.dosage.meal"
          :options="medicationMeal"
          :class="{'line-through': isDiscontinued}"
          :nullable="true"
          placeholder-text=""
          :test-id="getTestId('row-meal')"
          @change="updateMeal"
        />
      </MedTooltip>
    </div>

    <!-- row 3 - col 5 / Grund/Ind. -->
    <div class="col-span-4" :class="[{'pointer-events-none line-through': isDiscontinued}]">
      <MedicationComponentIndicationDisplay
        :indications="medicationForm.indications"
        :indications-unknown="medicationForm.indication_unknown"
        :is-active="isEditable"
        :is-discontinued="isDiscontinued"
        :disabled="!isEditable"
        :test-id="getTestId('row-discontinued')"
        @open-dialog="detailDialogIndications"
      />
    </div>

    <!-- row 3 - col 6 / Details -->
    <div class="col-span-1 flex h-9 items-center justify-center">
      <template v-if="isEditable">
        <MedicationComponentRowControl
          :data-hash-id="medicationForm.id"
          :show-badge="hasAdditionalInformation"
          :test-id="getTestId('row-open-details')"
          @click="openDetails"
        />

        <MedTooltip content="Zeile löschen">
          <MedButton
            aria-label="Medikament löschen"
            flat
            icon="delete"
            rounded="full"
            :data-test="getTestId('row-delete-button')"
            @click="showDeleteConfirmDialog"
          />
        </MedTooltip>
      </template>

      <template v-else>
        <div :data-test="getTestId('row-open-details')">
          <MedTooltip
            :content="hasAdditionalInformation ? 'Weitere Angaben sind hinterlegt' : 'Weitere / abweichende Angaben'"
          >
            <template v-if="hasAdditionalInformation">
              <ComponentBadge icon="info" class="text-mcgreen">
                <MedButton
                  aria-label="Weitere Angaben anzeigen"
                  variant="primary"
                  icon="more_horiz"
                  size="xs"
                  rounded="full"
                  :data-test="getTestId('row-open-details-button')"
                  @click="openDetails"
                />
              </ComponentBadge>
            </template>

            <template v-else>
              <MedButton
                aria-label="Details anzeigen"
                variant="primary"
                icon="more_horiz"
                size="xs"
                rounded="full"
                :data-test="getTestId('row-open-details-button')"
                @click="openDetails"
              />
            </template>
          </MedTooltip>
        </div>
      </template>
    </div>

    <!-- row 4 - expanded content -->
    <div v-if="expanded" class="col-span-12 mt-2 ml-8 text-sm">
      <dl>
        <div>
          <dt class="text-gray-500">Patientenangaben</dt>
          <dd>
            {{ detailsDeviantMedication }}
          </dd>
        </div>
      </dl>
    </div>

    <div v-if="expanded" class="col-span-6 mt-2 ml-8 text-sm">
      <dl class="space-y-2">
        <div>
          <dt class="text-gray-500">Hinweise zur abweichenden Anwendung (durch Patienten)</dt>
          <dd>{{ medication?.deviantmedication?.dosage?.info ?? "-" }}</dd>
        </div>
        <div>
          <dt class="text-gray-500">Unzufriedenheit</dt>
          <dd>{{ detailsSatisfaction }}</dd>
        </div>
        <div>
          <dt class="text-gray-500">Lagerung / Verfall / Anwendung</dt>
          <dd>{{ detailsSideEffects }}</dd>
        </div>
      </dl>
    </div>

    <div v-if="expanded" class="col-span-6 mt-2 ml-8 text-sm">
      <dl class="space-y-2">
        <div>
          <dt class="text-gray-500">Hinweise zur Medikation (wird in Medikationsplan übernommen)</dt>
          <dd>{{ medication?.dosage?.info ?? "-" }}</dd>
        </div>
        <div>
          <dt class="text-gray-500">Anforderungen Arzneiform</dt>
          <dd>{{ detailsRequirements }}</dd>
        </div>
        <div>
          <dt class="text-gray-500">Vorhanden in</dt>
          <dd>{{ detailsSource }}</dd>
        </div>
        <div>
          <dt class="text-gray-500">Verordnet durch / Herkunft</dt>
          <dd>{{ detailsDecreed }}</dd>
        </div>
      </dl>
    </div>
  </li>
</template>
